/**
 * Per-peer identity-verification state machine for 1:1 conversations.
 *
 * Mirrors Signal's behaviour: once a peer's identity key changes, sends are
 * blocked until the user explicitly acknowledges the new key.
 *
 * State transitions (driven by `applyKeyObservation`):
 *
 *   (no record)          ──[any key]───────────▶ 'tofu'               (allow)
 *   'tofu'(K)            ──[key=K]─────────────▶ 'tofu'(K)            (allow)
 *   'tofu'(K)            ──[key=K']────────────▶ 'changed'(prev=K,K') (BLOCK)
 *   'verified'(K)        ──[key=K]─────────────▶ 'verified'(K)        (allow)
 *   'verified'(K)        ──[key=K']────────────▶ 'changed'(prev=K,K') (BLOCK)
 *   'changed'(prev=P, C) ──[key=C]─────────────▶ 'changed'(prev=P,C)  (BLOCK)
 *   'changed'(prev=P, C) ──[key=C']────────────▶ 'changed'(prev=P,C') (BLOCK)
 *
 * Manual transitions (user actions):
 *
 *   any                  ──[markVerified()]────▶ 'verified'(current)   (allow)
 *   'changed'(_, C)      ──[acceptIdentityChange()]─▶ 'tofu'(C)        (allow)
 *
 * `acceptIdentityChange` moves to 'tofu' (not 'verified') to match iOS
 * behaviour: acceptance without a safety-number comparison grants implicit
 * trust only, not cryptographic verification. The user must call
 * `markVerified` again if they want the stronger 'verified' state.
 *
 * canSend policy: returns false iff trustState === 'changed'.
 * The encrypt path throws `IdentityChangedError` when canSend is false.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IdentityTrustState = "tofu" | "verified" | "changed";

/** Persisted record for one remote peer. */
export interface IdentityVerificationRecord {
  readonly userId: string;
  readonly trustState: IdentityTrustState;
  /** Hex-encoded SHA-256 of the peer's current identity public key bytes. */
  readonly identityPubKey: string;
  /** Hex-encoded SHA-256 of the previous identity public key, if any. */
  readonly previousPubKey: string | null;
  /** Epoch-ms timestamp of the last state-changing write. */
  readonly lastChangedAt: number;
  /** Epoch-ms timestamp when the user completed the safety-number check. */
  readonly verifiedAt?: number;
}

// ---------------------------------------------------------------------------
// Error
// ---------------------------------------------------------------------------

/**
 * Thrown by `e2eeEncrypt` (and the lower-level `encryptForPeer`) when the
 * recipient's identity key has changed and the user has not yet acknowledged
 * the change.
 */
export class IdentityChangedError extends Error {
  constructor(public readonly userId: string) {
    super(
      `[IdentityVerification] Send blocked: identity key changed for user '${userId}'. ` +
        "Call acceptIdentityChange() or markVerified() before sending.",
    );
    this.name = "IdentityChangedError";
  }
}

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

/**
 * Pure function — no I/O. Returns true when encryption is permitted,
 * false when the state machine requires user intervention.
 */
export function canSend(record: IdentityVerificationRecord): boolean {
  return record.trustState !== "changed";
}

// ---------------------------------------------------------------------------
// Hex helpers (no SubtleCrypto — sync, pure, no I/O)
// ---------------------------------------------------------------------------

/**
 * Produces a deterministic hex string from arbitrary bytes (Uint8Array or
 * ArrayBuffer).  Used as the storage key for identity public keys so that
 * comparison is always done on normalised strings rather than on ArrayBuffer
 * identity.
 */
export function pubKeyToHex(key: Uint8Array | ArrayBuffer): string {
  const bytes =
    key instanceof Uint8Array ? key : new Uint8Array(key as ArrayBuffer);
  let hex = "";
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

// ---------------------------------------------------------------------------
// IndexedDB store
// ---------------------------------------------------------------------------

const DB_NAME_DEFAULT = "kaether:identity-verification:v1";
const STORE_NAME = "records";
const DB_VERSION = 1;

function openDB(dbName: string): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "userId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(
  db: IDBDatabase,
  userId: string,
): Promise<IdentityVerificationRecord | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(userId);
    req.onsuccess = () =>
      resolve(req.result as IdentityVerificationRecord | undefined);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(
  db: IDBDatabase,
  record: IdentityVerificationRecord,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const req = tx.objectStore(STORE_NAME).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

function idbGetAll(
  db: IDBDatabase,
): Promise<IdentityVerificationRecord[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () =>
      resolve(req.result as IdentityVerificationRecord[]);
    req.onerror = () => reject(req.error);
  });
}

/**
 * IndexedDB-backed store for per-peer identity verification records.
 *
 * Instantiate with `await IdentityVerificationStore.open()`.
 * Pass a custom `dbName` in tests to get isolation without teardown races.
 */
export class IdentityVerificationStore {
  private constructor(private readonly db: IDBDatabase) {}

  static async open(
    dbName: string = DB_NAME_DEFAULT,
  ): Promise<IdentityVerificationStore> {
    const db = await openDB(dbName);
    return new IdentityVerificationStore(db);
  }

  // -------------------------------------------------------------------------
  // Reads
  // -------------------------------------------------------------------------

  async get(userId: string): Promise<IdentityVerificationRecord | undefined> {
    return idbGet(this.db, userId);
  }

  async list(): Promise<IdentityVerificationRecord[]> {
    return idbGetAll(this.db);
  }

  // -------------------------------------------------------------------------
  // State-machine transitions driven by key observation
  // -------------------------------------------------------------------------

  /**
   * Record a freshly-observed identity key for `userId`.
   *
   * - No prior record: creates a 'tofu' record.
   * - 'tofu' or 'verified' with same key: no-op (returns existing record).
   * - 'tofu' or 'verified' with different key: transitions to 'changed'.
   * - 'changed' with same blocked key: no-op (idempotent).
   * - 'changed' with a yet-newer key: refreshes currentKey; previous stays
   *   as the last-trusted key so the user knows what they originally verified.
   */
  async applyKeyObservation(
    userId: string,
    newKeyHex: string,
    now: number = Date.now(),
  ): Promise<IdentityVerificationRecord> {
    const existing = await idbGet(this.db, userId);

    if (!existing) {
      // First time we see this peer — TOFU.
      const record: IdentityVerificationRecord = {
        userId,
        trustState: "tofu",
        identityPubKey: newKeyHex,
        previousPubKey: null,
        lastChangedAt: now,
      };
      await idbPut(this.db, record);
      return record;
    }

    const { trustState, identityPubKey } = existing;

    if (trustState === "tofu" || trustState === "verified") {
      if (identityPubKey === newKeyHex) {
        // Stable observation — nothing changes.
        return existing;
      }
      // Key changed: block until user reacts.
      const record: IdentityVerificationRecord = {
        userId,
        trustState: "changed",
        identityPubKey: newKeyHex,
        previousPubKey: identityPubKey,
        lastChangedAt: now,
      };
      await idbPut(this.db, record);
      return record;
    }

    // trustState === 'changed'
    if (identityPubKey === newKeyHex) {
      // Same blocked key — no-op; preserve original lastChangedAt so the
      // idempotency invariant holds across write/read boundaries.
      return existing;
    }
    // Attacker rotating again — keep blocked, refresh currentKey, preserve
    // previousPubKey as the last trusted key.
    const record: IdentityVerificationRecord = {
      userId,
      trustState: "changed",
      identityPubKey: newKeyHex,
      previousPubKey: existing.previousPubKey,
      lastChangedAt: now,
    };
    await idbPut(this.db, record);
    return record;
  }

  // -------------------------------------------------------------------------
  // Manual transitions
  // -------------------------------------------------------------------------

  /**
   * Mark the current key for `userId` as verified (safety-number confirmed).
   * Works from any state including 'changed' — the user is explicitly
   * confirming that the current key is trusted.
   *
   * Throws `Error('not-found')` when no record exists for `userId`.
   */
  async markVerified(
    userId: string,
    now: number = Date.now(),
  ): Promise<IdentityVerificationRecord> {
    const existing = await idbGet(this.db, userId);
    if (!existing) {
      throw new Error("not-found");
    }
    const record: IdentityVerificationRecord = {
      ...existing,
      trustState: "verified",
      lastChangedAt: now,
      verifiedAt: now,
    };
    await idbPut(this.db, record);
    return record;
  }

  /**
   * Accept a key change without performing a full safety-number comparison.
   * Moves from 'changed' to 'tofu' — implicit trust on the new key.
   *
   * For full cryptographic verification, call `markVerified` instead (or
   * call this followed by `markVerified` after the safety-number flow).
   *
   * Throws `Error('not-found')` when no record exists.
   * Throws `Error('no-change-to-accept')` when state is not 'changed'.
   */
  async acceptIdentityChange(
    userId: string,
    now: number = Date.now(),
  ): Promise<IdentityVerificationRecord> {
    const existing = await idbGet(this.db, userId);
    if (!existing) {
      throw new Error("not-found");
    }
    if (existing.trustState !== "changed") {
      throw new Error("no-change-to-accept");
    }
    const record: IdentityVerificationRecord = {
      userId,
      trustState: "tofu",
      identityPubKey: existing.identityPubKey,
      previousPubKey: existing.previousPubKey,
      lastChangedAt: now,
    };
    await idbPut(this.db, record);
    return record;
  }

  close(): void {
    this.db.close();
  }
}
